import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { PrintDao } from "@/data/dao/service/PrintDao";
import { Language, ReceiptType } from "@/data/model/Enums";
import { exceptionTypes } from "@/data/util/Config";

import { BaseModal } from "@/shared/elements/BaseModal";
import { languageSelectList } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export class PrintReceiptWindow extends BaseModal {
    private printDao = Factory.getInstance<PrintDao>(PrintDao);
    private readonly sourceId: string;
    private readonly receiptType: ReceiptType;
    private readonly locationLanguage?: Language;
    private $form: JQuery;
    private $submitBtn: JQuery;

    private languageSelect: Select<Language>;

    constructor(sourceId: string, receiptType: ReceiptType, locationLanguage: Language | undefined) {
        super(i18n("modal.printOrderReceipt.title"), "modal-lg", false);
        this.sourceId = sourceId;
        this.receiptType = receiptType;
        this.locationLanguage = locationLanguage;

        this.$element.addClass("printorderreceipt");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.sendOrderReceipt.settings") +
                "</h3></div></div>"
        );

        const languageGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.fields.language") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const select = $('<select class="form-control" />').appendTo(languageGroup);
        languageSelectList.forEach((item) => {
            select.append(`<option value="${item.id}">${i18n("enums.Language." + item.name)}</option>`);
        });
        this.languageSelect = Select.createFromSelect(select);
        this.languageSelect.setValue(
            this.locationLanguage ?? TastierGlobal.getLocationSwitch()?.getCurrentLocationLanguage() ?? Language.English
        );

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.send") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            switch (this.receiptType) {
                case ReceiptType.Order:
                    this.printOrderReceipt();
                    break;
                case ReceiptType.Payment:
                    this.printPaymentReceipt();
                    break;
            }

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {}

    private async printPaymentReceipt() {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.printDao.printPaymentReceipt(this.sourceId, {
                locationId: TastierGlobal.getLocationSwitch()!.getCurrentLocationId(),
                language: parseInt(this.languageSelect.getValue()!),
            });

            UiNotification.showSuccess(i18n("print.orderReceiptPrinted"));
            this.close();
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.noProductionSiteForRoomEnabledException) {
                UiNotification.showException(i18n("print.noProductionSiteEnabledForRoom"));
            } else {
                UiNotification.showException(i18n("print.failedPrintReceipt"));
            }
        } finally {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
        }
    }

    private async printOrderReceipt() {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.printDao.printOrderReceipt(this.sourceId, {
                locationId: TastierGlobal.getLocationSwitch()!.getCurrentLocationId(),
                language: parseInt(this.languageSelect.getValue()!),
            });

            UiNotification.showSuccess(i18n("print.orderReceiptPrinted"));
            this.close();
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.noProductionSiteForRoomEnabledException) {
                UiNotification.showException(i18n("print.noProductionSiteEnabledForRoom"));
            } else {
                UiNotification.showException(i18n("print.failedPrintReceipt"));
            }
        } finally {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
        }
    }
}
